import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FORUM_CATEGORIES, forumApi } from "../../utils/forumApi";
import QuestionCard from "./QuestionCard";
import {
  ContentGrid,
  ControlsCard,
  EmptyState,
  EmptyText,
  EmptyTitle,
  ForumSection,
  ForumShell,
  HeroActions,
  HeroCard,
  HeroEyebrow,
  HeroText,
  HeroTitle,
  MainColumn,
  Panel,
  PanelText,
  PanelTitle,
  PaginationButtons,
  PageNumberButton,
  PaginationRow,
  PaginationText,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  Select,
  Sidebar,
} from "./ForumStyles";

const categoryOptions = ["All", ...FORUM_CATEGORIES];

function ForumPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({
    questions: [],
    pagination: { page: 1, total: 0, totalPages: 1 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const currentFilters = useMemo(
    () => ({
      search: searchParams.get("search") || "",
      category: searchParams.get("category") || "All",
      page: Number(searchParams.get("page") || "1"),
    }),
    [searchParams]
  );

  useEffect(() => {
    let isMounted = true;

    const loadQuestions = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await forumApi.getQuestions({
          search: currentFilters.search,
          category:
            currentFilters.category && currentFilters.category !== "All"
              ? currentFilters.category
              : "",
          page: currentFilters.page,
        });

        if (isMounted) {
          setData({
            questions: response.questions || [],
            pagination: response.pagination || {
              page: 1,
              total: 0,
              totalPages: 1,
            },
          });
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(
            fetchError instanceof Error
              ? fetchError.message
              : "Could not load forum questions."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadQuestions();

    return () => {
      isMounted = false;
    };
  }, [currentFilters]);

  const updateParams = (updates) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      const normalized = String(value ?? "").trim();
      if (!normalized || (key === "category" && normalized === "All")) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, normalized);
      }
    });

    if (!("page" in updates)) {
      nextParams.set("page", "1");
    }

    if (nextParams.get("page") === "1") {
      nextParams.delete("page");
    }

    setSearchParams(nextParams);
  };

  const pageNumbers = Array.from(
    { length: data.pagination.totalPages || 1 },
    (_, index) => index + 1
  );

  return (
    <ForumSection>
      <ForumShell>
        <HeroCard>
          <HeroEyebrow>Mags Community Forum</HeroEyebrow>
          <HeroTitle>Ask practical engineering questions and learn from real field experience.</HeroTitle>
          <HeroText>
            The forum is for technical questions around engineering training, plant
            operations, instrumentation, safety, and industrial equipment. Questions
            are reviewed before they appear publicly.
          </HeroText>
          <HeroActions>
            <PrimaryButton to="/forum/ask">Ask a Question</PrimaryButton>
            <SecondaryButton to="/training">Browse Training</SecondaryButton>
          </HeroActions>
        </HeroCard>

        <ControlsCard>
          <SearchInput
            type="search"
            placeholder="Search by title or question details"
            value={currentFilters.search}
            onChange={(event) =>
              updateParams({ search: event.target.value, page: "1" })
            }
          />

          <Select
            value={currentFilters.category}
            onChange={(event) =>
              updateParams({ category: event.target.value, page: "1" })
            }
          >
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category === "All" ? "All Categories" : category}
              </option>
            ))}
          </Select>
        </ControlsCard>

        <ContentGrid>
          <MainColumn>
            {error ? (
              <EmptyState>
                <EmptyTitle>Could not load the forum</EmptyTitle>
                <EmptyText>{error}</EmptyText>
              </EmptyState>
            ) : null}

            {!error && isLoading ? (
              <EmptyState>
                <EmptyTitle>Loading questions...</EmptyTitle>
                {/* <EmptyText>Please wait while we fetch the latest approved discussions.</EmptyText> */}
              </EmptyState>
            ) : null}

            {!error && !isLoading && data.questions.length === 0 ? (
              <EmptyState>
                <EmptyTitle>No public questions yet</EmptyTitle>
                <EmptyText>
                  Be the first to start a discussion.
                </EmptyText>
              </EmptyState>
            ) : null}

            {!error && !isLoading
              ? data.questions.map((question) => (
                  <QuestionCard key={question.id} question={question} />
                ))
              : null}

            {!error && !isLoading && data.questions.length > 0 ? (
              <Panel>
                <PaginationRow>
                  <PaginationText>
                    Showing page {data.pagination.page} of {data.pagination.totalPages}
                  </PaginationText>
                  <PaginationButtons>
                    {pageNumbers.map((pageNumber) => (
                      <PageNumberButton
                        key={pageNumber}
                        type="button"
                        $active={pageNumber === data.pagination.page}
                        onClick={() =>
                          updateParams({ page: String(pageNumber) })
                        }
                      >
                        {pageNumber}
                      </PageNumberButton>
                    ))}
                  </PaginationButtons>
                </PaginationRow>
              </Panel>
            ) : null}
          </MainColumn>

          <Sidebar>
            <Panel>
              <PanelTitle>Forum Guidelines</PanelTitle>
              <PanelText>
                Write clear technical questions, include context, and explain what you
                have already tried. That helps the right people give better answers.
              </PanelText>
            </Panel>

            <Panel>
              <PanelTitle>What belongs here</PanelTitle>
              <PanelText>
                Mechanical, process, petroleum and petrochemical engineering,
                HAZOP and HAZID studies, project management consultancy,
                commissioning, drafting and modelling, and process control or
                instrumentation questions.
              </PanelText>
            </Panel>
          </Sidebar>
        </ContentGrid>
      </ForumShell>
    </ForumSection>
  );
}

export default ForumPage;
